"use client"

import { useCallback, useEffect, useState } from "react"
import { ArrowUpRight } from "lucide-react"
import { apiUrl, authHeaders } from "@/lib/api"
import FetchError from "./fetch-error"

interface Scraper {
  name: string
  label: string
  status: "online" | "error" | "idle" | "warning"
  lastRun: string | null
  lastDuration: number | null
  itemsCollected: number
  errorMessage: string | null
}

function timeAgo(iso?: string | null): string {
  if (!iso) return "—"
  const diff = Date.now() - new Date(iso).getTime()
  const min = Math.floor(diff / 60000)
  if (min < 1) return "agora"
  if (min < 60) return `${min}m`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h}h`
  return `${Math.floor(h / 24)}d`
}

const statusDotColor: Record<Scraper["status"], string> = {
  online: "bg-emerald-500",
  warning: "bg-amber-500",
  error: "bg-rose-500",
  idle: "bg-muted-foreground/50",
}

const statusLabel: Record<Scraper["status"], string> = {
  online: "ativo",
  warning: "aviso",
  error: "erro",
  idle: "inativo",
}

export default function SystemHistory() {
  const [scrapers, setScrapers] = useState<Scraper[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [lastCheck, setLastCheck] = useState<string>("")

  const load = useCallback(() => {
    const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null
    fetch(`${apiUrl()}/system/health/`, { headers: authHeaders(token) })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then((data) => {
        setScrapers(data.scrapers || [])
        setLastCheck(new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }))
        setError(false)
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    // O card se apresenta como "ao vivo" — sem polling isso seria mentira.
    load()
    const id = setInterval(load, 60_000)
    return () => clearInterval(id)
  }, [load])

  const activeCount = scrapers.filter((s) => s.status === "online").length

  return (
    <div className="surface-card flex h-full flex-col">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Operação</div>
          <h3 className="mt-1 font-display text-2xl font-normal tracking-tight text-foreground">
            Scrapers ao vivo
          </h3>
        </div>
        <a
          href="/dashboard-cms/saude"
          className="group inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          Monitor completo
          <ArrowUpRight className="h-3 w-3 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </a>
      </div>

      <div className="mt-5 flex-1">
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <div key={i} className="h-12 animate-pulse rounded bg-muted/30" />)}
          </div>
        ) : error && scrapers.length === 0 ? (
          <FetchError what="a leitura dos robôs" onRetry={load} />
        ) : scrapers.length === 0 ? (
          <div className="py-8 text-sm text-muted-foreground">Nenhum robô de coleta registrado ainda.</div>
        ) : (
          <ul className="divide-y divide-border/40">
            {scrapers.slice(0, 8).map((s) => (
              <li key={s.name} className="group flex items-center gap-4 py-3 transition-colors hover:bg-muted/20 -mx-2 px-2 rounded-md">
                <span className="flex flex-shrink-0 items-center">
                  <span className={`h-2 w-2 rounded-full ${statusDotColor[s.status]}`} />
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="truncate text-sm font-medium text-foreground">{s.label}</span>
                    <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                      {statusLabel[s.status]}
                    </span>
                  </div>
                  {s.errorMessage && (
                    <div className="mt-0.5 truncate text-xs text-rose-600 dark:text-rose-400" title={s.errorMessage}>
                      {s.errorMessage}
                    </div>
                  )}
                </div>

                <div className="flex flex-shrink-0 items-center gap-3 font-mono text-[11px] text-muted-foreground">
                  {s.itemsCollected > 0 && (
                    <span>
                      <span className="text-foreground">{s.itemsCollected}</span> itens
                    </span>
                  )}
                  {s.lastDuration != null && (
                    <span>{s.lastDuration.toFixed(1)}s</span>
                  )}
                  <span className="text-foreground">{timeAgo(s.lastRun)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-border/40 pt-4 font-mono text-[11px] text-muted-foreground">
        <span>
          <span className="text-foreground">{activeCount}</span>/{scrapers.length} operacionais
        </span>
        {lastCheck && <span>leitura {lastCheck}</span>}
      </div>
    </div>
  )
}
